import { useCallback, useEffect, useRef, useState } from 'react';
import type { Product } from '@hygh-vybe/lib';
import { API_BASE } from '../config';

const PAGE_SIZE = 24;

export type MarketplaceSortOption =
  | 'sponsored_first'
  | 'highest_rated'
  | 'lowest_thc'
  | 'highest_thc'
  | 'newest'
  | 'alphabetical';

export interface MarketplaceFilters {
  query: string;
  potency: string | null;
  flavor: string | null;
  format: string | null;
  onsetTime: string | null;
  brand: string | null;
  sort: MarketplaceSortOption;
}

export interface MarketplaceProduct extends Product {
  affiliateLink?: {
    linkId: string;
    trackedUrl: string;
  } | null;
  discountCode?: string | null;
  discountDescription?: string | null;
}

export interface UseMarketplaceResult {
  products: MarketplaceProduct[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  loadMore: () => void;
}

function parsePotencyRange(potency: string | null): { min: string | null; max: string | null } {
  if (!potency) return { min: null, max: null };
  const [minStr, maxStr] = potency.split('-');
  return { min: minStr || null, max: maxStr || null };
}

function parseOnsetTimeRange(onset: string | null): { min: string | null; max: string | null } {
  if (!onset) return { min: null, max: null };
  const [minStr, maxStr] = onset.split('-');
  return { min: minStr || null, max: maxStr || null };
}

export function useMarketplace(filters: MarketplaceFilters): UseMarketplaceResult {
  const [products, setProducts] = useState<MarketplaceProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const buildUrl = useCallback(
    (nextCursor?: string | null) => {
      const params = new URLSearchParams();

      if (filters.query) params.set('q', filters.query);
      if (filters.format) params.set('format', filters.format);
      if (filters.flavor) params.set('flavor', filters.flavor);
      if (filters.brand) params.set('brand', filters.brand);

      const potency = parsePotencyRange(filters.potency);
      if (potency.min) params.set('potencyMin', potency.min);
      if (potency.max) params.set('potencyMax', potency.max);

      const onset = parseOnsetTimeRange(filters.onsetTime);
      if (onset.min) params.set('onsetMin', onset.min);
      if (onset.max) params.set('onsetMax', onset.max);

      params.set('sort', filters.sort);
      params.set('limit', String(PAGE_SIZE));
      if (nextCursor) params.set('cursor', nextCursor);

      return `${API_BASE}/v1/marketplace?${params.toString()}`;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.query, filters.format, filters.flavor, filters.brand, filters.potency, filters.onsetTime, filters.sort]
  );

  // Fetch initial results when filters change
  useEffect(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    async function fetchProducts() {
      setLoading(true);
      setError(null);

      try {
        const url = buildUrl(null);
        const res = await fetch(url, { signal: controller.signal });

        if (!res.ok) {
          throw new Error(`Failed to load marketplace (${res.status})`);
        }

        const json = await res.json();
        const data = json.data ?? [];
        const meta = json.metadata;

        setProducts(data);
        setTotalCount(meta?.pagination?.totalCount ?? data.length);
        setCursor(meta?.pagination?.nextCursor ?? null);
        setHasMore(!!meta?.pagination?.nextCursor);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Something went wrong');
        setProducts([]);
        setTotalCount(0);
        setHasMore(false);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchProducts();

    return () => {
      controller.abort();
    };
  }, [buildUrl]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;

    setLoadingMore(true);
    try {
      const url = buildUrl(cursor);
      const res = await fetch(url);

      if (!res.ok) {
        throw new Error(`Failed to load more (${res.status})`);
      }

      const json = await res.json();
      const data = json.data ?? [];
      const meta = json.metadata;

      setProducts((prev) => [...prev, ...data]);
      setCursor(meta?.pagination?.nextCursor ?? null);
      setHasMore(!!meta?.pagination?.nextCursor);
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setError(err instanceof Error ? err.message : 'Failed to load more');
      }
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, buildUrl]);

  return { products, loading, loadingMore, error, hasMore, totalCount, loadMore };
}
